import React from 'react';
import {Link} from 'react-router-dom';
import styles from './DataRow.module.css';
import RowMenu from './RowMenu';

const PlaceholderThumb = () => (
    <svg width="40" height="26" viewBox="0 0 40 26" aria-hidden="true">
        <rect x="2" y="2" width="36" height="22" rx="3" fill="none" stroke="#C9C6BC" strokeWidth="1.4"/>
        <ellipse cx="16" cy="13" rx="7" ry="4" fill="#F1EFE8" stroke="#C9C6BC" strokeWidth="1.2"/>
    </svg>
);

const CheckIcon = (props) => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
        <path d="M5 12l5 5L20 7"/>
    </svg>
);

const OpenIcon = (props) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
        <path d="M14 4h6v6M20 4l-9 9M18 14v5H5V6h5"/>
    </svg>
);

/**
 * One row of a DataList. `thumbnail`, `folder`, `jiraKey`, `progress` and
 * `owner` are all optional - when the caller doesn't have the data yet
 * (see Pages.js), the row degrades gracefully instead of faking it.
 *
 * A single grid of six cells (check/page/progress/owner/updated/actions);
 * CSS reflows which cells are visible and where on mobile, so there is only
 * ever one of each interactive control (checkbox, Open link, ⋮ menu) - never
 * a desktop/mobile pair with the same accessible name fighting for focus.
 */
const DataRow = ({
    id,
    name,
    href,
    thumbnail,
    folder,
    jiraKey,
    updated,
    progress,
    complete,
    owner,
    selected,
    onSelectChange,
    selectable = true,
    menuOpen,
    onMenuOpenChange,
    menuItems,
}) => {
    const total = progress ? progress.done + progress.doing + progress.todo : 0;
    const progressLabel = progress
        ? `${progress.done} done${progress.doing ? `, ${progress.doing} doing` : ''}${progress.todo ? `, ${progress.todo} to do` : ''}`
        : null;

    return (
        <div role="row" aria-selected={selected || undefined} className={`${styles.row} ${selected ? styles.selected : ''}`}>
            <span className={styles.checkCell}>
                {selectable && (
                    <input
                        type="checkbox"
                        checked={!!selected}
                        onChange={(e) => onSelectChange(e.target.checked)}
                        aria-label={`Select ${name}`}
                        className={styles.checkbox}
                    />
                )}
            </span>

            <div className={styles.pageCell}>
                <span className={styles.thumb}>{thumbnail || <PlaceholderThumb/>}</span>
                <div className={styles.nameCol}>
                    <Link to={href} className={styles.nameLink}>{name}</Link>
                    <span className={styles.metaDesktop}>
                        {jiraKey && <span className={styles.jiraChip}>{jiraKey}</span>}
                        {folder}
                    </span>
                    <span className={styles.metaMobile}>
                        {folder}{folder && updated ? ' · ' : ''}{updated}
                    </span>
                </div>
            </div>

            <div className={styles.progressCell}>
                {complete ? (
                    <span className={styles.completePill}><CheckIcon/>Complete</span>
                ) : progress ? (
                    <>
                        <span role="img" aria-label={progressLabel} className={styles.progressBar}>
                            {progress.done > 0 && <span className={styles.segDone} style={{flexGrow: progress.done}}/>}
                            {progress.doing > 0 && <span className={styles.segDoing} style={{flexGrow: progress.doing}}/>}
                            {progress.todo > 0 && <span className={styles.segTodo} style={{flexGrow: progress.todo}}/>}
                        </span>
                        <span className={styles.progressText}>{progress.done} of {total} done</span>
                    </>
                ) : null}
                {owner && <span className={styles.ownerMobile} title={owner.name} style={{background: owner.colour}}>{owner.initials}</span>}
            </div>

            {owner ? (
                <span className={styles.ownerCell} title={owner.name} style={{background: owner.colour}}>
                    <span className={styles.srOnly}>{owner.name}</span>
                    <span aria-hidden="true">{owner.initials}</span>
                </span>
            ) : <span className={styles.ownerCell}/>}

            <span className={styles.updatedCell}>{updated}</span>

            <div className={styles.actionsCell}>
                <Link to={href} className={styles.openLink}><OpenIcon/>Open</Link>
                <RowMenu label={name} items={menuItems} open={menuOpen} onOpenChange={onMenuOpenChange}/>
            </div>
        </div>
    );
};

export default DataRow;
